import React from 'react';
import { TextCTA } from '../components/ui/TextCTA';
import { Section, SectionHeading } from '../components/ui/Section';
import { Prose, ProseParagraph, ProseList } from '../components/ui/Prose';
import { PullQuote, SectionDivider } from '../components/ui/PullQuote';

const HomePage = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-8 md:pb-12 border-b border-border">
        <div className="editorial-container">
          <h1 className="mb-4 text-balance">Coherence OS</h1>
          <p className="font-serif text-xl md:text-2xl italic text-muted-foreground mb-6 max-w-2xl">
            An Operating System for Civilization-Scale Alignment
          </p>
          
          <Prose>
            <ProseParagraph>
              Coherence OS is a teleology-first (purpose-governed) operating system that governs how decisions are made and actions are taken—ensuring systems remain aligned to human dignity, planetary viability, and long-term coherence as complexity and AI acceleration increase.
            </ProseParagraph>
          </Prose>
          
          {/* Primary Paths */}
          <div className="mt-6 flex flex-col gap-3">
            <TextCTA href="/the-category">Explore the category</TextCTA>
            <TextCTA href="mailto:builders@coherenceos.com?subject=Builder%20Alignment" external>
              Signal builder alignment
            </TextCTA>
            <TextCTA href="mailto:investors@coherenceos.com?subject=Long-Horizon%20Investor%20Alignment" external>
              Signal long-horizon investor alignment
            </TextCTA>
          </div>
        </div>
      </section>

      {/* The pause most systems never take */}
      <Section>
        <SectionHeading>The pause most systems never take</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Before any system is built, scaled, or optimized, there is a question that quietly determines the future:
          </ProseParagraph>
          
          <PullQuote>
            What world does this system make inevitable by the way it is designed to succeed?
          </PullQuote>
          
          <ProseParagraph>
            Not merely: Can it grow? Will it scale? Is it efficient?
          </ProseParagraph>
          
          <ProseParagraph>
            But:
          </ProseParagraph>
          
          <ProseList items={[
            "Does it preserve human dignity over time?",
            "Does it sustain the conditions for life?",
            "Does it increase coherence rather than fragmentation?",
            "Does it remain viable across generations—or externalize harm onto unseen others?"
          ]} />
          
          <ProseParagraph>
            Humanity has always built systems whose consequences outlive their creators.
          </ProseParagraph>
          
          <ProseParagraph>
            What is new is <strong>how quickly</strong>, <strong>how globally</strong>, and <strong>how autonomously</strong> those consequences now unfold—often without any governing layer capable of discerning or correcting them.
          </ProseParagraph>
          
          <ProseParagraph>
            Coherence OS exists to make that discernment <strong>structural</strong> rather than retrospective—by governing the systems themselves, so the futures they generate are shaped by design, not left to intention, hope, or after-the-fact correction.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* Why system design now shapes civilization */}
      <Section>
        <SectionHeading>Why system design now shapes civilization</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Modern civilization is no longer shaped primarily by individual intent, but by the systems that translate intent into action at scale.
          </ProseParagraph>
          
          <ProseParagraph>
            It is shaped by <strong>system defaults</strong>.
          </ProseParagraph>
          
          <ProseParagraph>
            Defaults determine:
          </ProseParagraph>
          
          <ProseList items={[
            "what behavior is rewarded",
            "what tradeoffs are normalized",
            "what harms remain invisible",
            'what futures quietly become "standard"'
          ]} />
          
          <ProseParagraph>
            As intelligence and automation accelerate, defaults harden faster than reflection.
          </ProseParagraph>
          
          <ProseParagraph>
            Coherence OS exists to <strong>govern how defaults are formed</strong>—so what becomes inevitable remains compatible with human dignity, planetary viability, and long-term coherence.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* A simple definition */}
      <Section>
        <SectionHeading>A simple definition</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Coherence OS is a teleology-first operating system for decision-making and execution.
          </ProseParagraph>
          
          <ProseParagraph>
            It helps people and organizations establish <strong>direction</strong>—identity, purpose, and non-negotiable constraints—and then ensures decisions and actions remain aligned to the conditions required for civilization to endure.
          </ProseParagraph>
          
          <PullQuote>
            Most systems optimize output. Coherence OS governs direction under constraint.
          </PullQuote>
        </Prose>
      </Section>

      <SectionDivider />

      {/* What "governing direction" means */}
      <Section>
        <SectionHeading>What "governing direction" means</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Governing direction does not mean imposing beliefs or preferences.
          </ProseParagraph>
          
          <ProseParagraph>
            It means recognizing that <strong>certain conditions must be satisfied</strong> if systems are to contribute to a world that works for everyone.
          </ProseParagraph>
          
          <ProseParagraph>
            These conditions include:
          </ProseParagraph>
          
          <ProseList items={[
            "preservation of human dignity",
            "sustainability of life-supporting systems",
            "coherence across time horizons",
            "avoidance of harm displaced onto others"
          ]} />
          
          <ProseParagraph>
            Optimization is permitted <strong>only within</strong> these constraints.
          </ProseParagraph>
          
          <ProseParagraph>
            This is not moral positioning. It is civilizational necessity.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* Why this matters now */}
      <Section>
        <SectionHeading>Why this matters now</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            We are entering an era where:
          </ProseParagraph>
          
          <ProseList items={[
            "intelligence scales faster than human oversight",
            "systems act with increasing autonomy",
            "consequences propagate globally and irreversibly"
          ]} />
          
          <ProseParagraph>
            For the first time, <strong>the pace of system action routinely exceeds the pace of human correction</strong>.
          </ProseParagraph>
          
          <ProseParagraph>
            Without a governing layer for direction, systems become powerful without coherence.
          </ProseParagraph>
          
          <ProseParagraph>
            Coherence OS is that governing layer.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* What becomes possible under constraint */}
      <Section>
        <SectionHeading>What becomes possible under constraint</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            When systems are governed by direction before optimization, different futures become possible:
          </ProseParagraph>
          
          <div className="space-y-6 mt-8">
            <div>
              <h4 className="font-serif text-lg font-medium mb-2">Work & human dignity</h4>
              <ProseParagraph className="!mb-0">
                Systems can reward meaningful contribution and sustainability—not depletion and burnout.
              </ProseParagraph>
            </div>
            
            <div>
              <h4 className="font-serif text-lg font-medium mb-2">Education & human potential</h4>
              <ProseParagraph className="!mb-0">
                Learning systems can strengthen identity coherence and long-term contribution—not just ranking and throughput.
              </ProseParagraph>
            </div>
            
            <div>
              <h4 className="font-serif text-lg font-medium mb-2">Ethical AI governance</h4>
              <ProseParagraph className="!mb-0">
                Automation can be constrained by consequence and dignity—not just capability.
              </ProseParagraph>
            </div>
            
            <div>
              <h4 className="font-serif text-lg font-medium mb-2">Planetary stewardship</h4>
              <ProseParagraph className="!mb-0">
                Economic systems can treat regeneration as success—not an externality.
              </ProseParagraph>
            </div>
          </div>
          
          <ProseParagraph className="mt-8">
            These are not features. They are outcomes of <strong>orientation governing optimization</strong>.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* What Coherence OS is not */}
      <Section>
        <SectionHeading>What Coherence OS is not</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Coherence OS is not:
          </ProseParagraph>
          
          <ProseList items={[
            "a productivity tool",
            "a coaching platform",
            "a model wrapper",
            "a neutral system"
          ]} />
          
          <ProseParagraph>
            It is a <strong>governing layer</strong>, designed only for systems aligned to the conditions that sustain life, dignity, and coherence.
          </ProseParagraph>
          
          <ProseParagraph>
            If a system's success would erode those conditions, Coherence OS is incompatible with it. This boundary is intentional.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* Protection and stewardship */}
      <Section>
        <SectionHeading>Protection and stewardship</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Coherence OS is protected by <strong>filed and pending patents</strong> covering its core architectural approach.
          </ProseParagraph>
          
          <ProseParagraph>
            This protection exists to preserve:
          </ProseParagraph>
          
          <ProseList items={[
            "the integrity of the category",
            "constraint-based governance",
            "responsible stewardship at scale"
          ]} />
          
          <ProseParagraph>
            Quietly. Deliberately.
          </ProseParagraph>
        </Prose>
      </Section>

      <SectionDivider />

      {/* Who this is for */}
      <Section>
        <SectionHeading>Who this is for</SectionHeading>
        
        <Prose>
          <ProseParagraph>
            Coherence OS is for builders, institutions, and stewards who recognize that:
          </ProseParagraph>
          
          <ProseList items={[
            "systems shape civilization through defaults",
            "direction must be governed before speed",
            "coherence is a design requirement, not a hope"
          ]} />
          
          <ProseParagraph>
            If that recognition resonates, you are in the right place.
          </ProseParagraph>
        </Prose>
        
        {/* Next paths */}
        <div className="mt-10 pt-8 border-t border-border">
          <p className="text-sm text-muted-foreground uppercase tracking-wider mb-4">
            Next paths
          </p>
          <div className="flex flex-col gap-3">
            <TextCTA href="/the-category">Explore the category</TextCTA>
            <TextCTA href="mailto:builders@coherenceos.com?subject=Builder%20Alignment" external>
              Signal builder alignment
            </TextCTA>
            <TextCTA href="mailto:investors@coherenceos.com?subject=Long-Horizon%20Investor%20Alignment" external>
              Signal long-horizon investor alignment
            </TextCTA>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default HomePage;
